
import { Component } from 'react';
import { OptionPathToUrlMapper } from 'xconvert-web-shared';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { SearchableDropdown } from '../extension-select-dropdown/extension-select-dropdown.component';
import { FromToRowData, ProcessSelectionRawData, ToRowData } from '../models/process-selection-row-data';
import { ProcessSelectionDropdown } from '../process-select-dropdown/process-select-dropdown.component';
import styles from './feature-select-dropdown.component.module.scss';



/**
 * Constructs a URL path from process/from/to using OptionPathToUrlMapper
 * for consistent URL mapping across the application.
 */
function getURL(process: string, from?: string, to?: string): string | null {
    let optionPath: string = null;

    if (process && from && to) {
        optionPath = `${process}-${from}-to-${to}`.toLowerCase();
    } else if (process && to) {
        optionPath = `${process}-${to}`.toLowerCase();
    }

    if (!optionPath) {
        return null;
    }

    // Use shared OptionPathToUrlMapper for consistent URL mapping
    const mappedPath = OptionPathToUrlMapper.mapToUrlPath(optionPath);
    return `/${mappedPath}`;
}

interface PageSelectionDropdownProps {
    options: ProcessSelectionRawData[];
    pageUrl: string | null;
    onChange: (value: any) => void;
    onSubmit: (value: any) => void;
}

interface PageSelectionDropdownState {
    selectedUrl: string | null;
    selectedProcess: ProcessSelectionRawData | null;

    selectedFromToOption: FromToRowData | null;
    selectedFromToOptionValue: string | null;
    selectedToOption: ToRowData | null;
    selectedToOptionValue: string | null;

    selectedToOnlyOption: ToRowData | null;
    selectedToOnlyOptionValue: string | null;
    hasFromOption: Boolean;
}

export class PageSelectionDropdown extends Component<PageSelectionDropdownProps, PageSelectionDropdownState> {

    private static STORAGE_KEY = 'xconvert_page_selection_v1';

    constructor(props: PageSelectionDropdownProps) {
        super(props);

        // Initialize state with default or first values
        const initialProcess = props.options[0];
        this.state = this.buildStateFromProcess(initialProcess);
    }

    componentDidMount() {
        this.restoreSelection();
    }

    private restoreSelection() {
        if (!BrowserUtils.hasWindow()) return;

        try {
            const saved = localStorage.getItem(PageSelectionDropdown.STORAGE_KEY);
            if (saved) {
                const { processId, from, to } = JSON.parse(saved);
                const process = this.props.options.find(p => p.process === processId);

                if (process) {
                    // Try to rebuild state to match specific from/to
                    this.rebuildStateForSelection(process, from, to);
                }
            }
        } catch (e) {
            console.error("Failed to restore selection", e);
        }
    }

    private saveSelection() {
        if (!BrowserUtils.hasWindow()) return;

        const selection = {
            processId: this.state.selectedProcess?.process,
            from: this.state.selectedFromToOptionValue,
            to: this.state.selectedToOptionValue ?? this.state.selectedToOnlyOptionValue
        };
        localStorage.setItem(PageSelectionDropdown.STORAGE_KEY, JSON.stringify(selection));
    }

    private buildStateFromProcess(process: ProcessSelectionRawData): PageSelectionDropdownState {
        const hasFromOption = (process.fromToMediaOptions?.length ?? 0) > 0;
        return {
            selectedUrl: null,
            selectedProcess: process,
            selectedFromToOption: hasFromOption ? process.fromToMediaOptions[0] : null,
            selectedFromToOptionValue: hasFromOption ? process.fromToMediaOptions[0].options[0]?.value : null,
            selectedToOption: hasFromOption ? process.fromToMediaOptions[0].toOptions[0] : null,
            selectedToOptionValue: hasFromOption ? process.fromToMediaOptions[0].toOptions[0].options[0]?.value : null,
            selectedToOnlyOption: !hasFromOption ? process.toOnlyMediaOptions[0] : null,
            selectedToOnlyOptionValue: !hasFromOption ? process.toOnlyMediaOptions[0].options[0]?.value : null,
            hasFromOption: hasFromOption,
        };
    }

    private rebuildStateForSelection(process: ProcessSelectionRawData, from: string | null, to: string | null) {
        let newState = this.buildStateFromProcess(process);

        if (newState.hasFromOption && from) {
            const fromOption = process.fromToMediaOptions?.find(o => o.options.some(item => item.value === from));
            if (fromOption) {
                newState.selectedFromToOption = fromOption;
                newState.selectedFromToOptionValue = from;

                if (to) {
                    const toOption = fromOption.toOptions?.find(o => o.options.some(item => item.value === to));
                    if (toOption) {
                        newState.selectedToOption = toOption;
                        newState.selectedToOptionValue = to;
                    }
                } else {
                    newState.selectedToOption = fromOption.toOptions[0];
                    newState.selectedToOptionValue = fromOption.toOptions[0].options[0]?.value;
                }
            }
        } else if (!newState.hasFromOption && to) {
            const toOption = process.toOnlyMediaOptions?.find(o => o.options.some(item => item.value === to));
            if (toOption) {
                newState.selectedToOnlyOption = toOption;
                newState.selectedToOnlyOptionValue = to;
            }
        }

        this.setState(newState, () => this.onChange());
    }

    handleProcessTypeChange(value: ProcessSelectionRawData) {
        const newState = this.buildStateFromProcess(value);
        this.setState(newState, () => {
            this.onChange();
        });
    }

    handleFromToOptionChange(selectedFromToOption: ToRowData, selectedFromOption: string): void {
        const toOptions = (selectedFromToOption as FromToRowData).toOptions;
        var selectedToOption = toOptions[0];
        var selectedToOptionValue = selectedToOption.options[0]?.value;

        if (toOptions.find(it => it === this.state.selectedToOption)) {
            selectedToOption = this.state.selectedToOption;
            selectedToOptionValue = this.state.selectedToOptionValue ?? toOptions[0].options[0]?.value;
        }

        this.setState(
            {
                selectedFromToOption: selectedFromToOption as FromToRowData,
                selectedFromToOptionValue: selectedFromOption,
                selectedToOption,
                selectedToOptionValue,
                selectedToOnlyOption: null,
                selectedToOnlyOptionValue: null,
                hasFromOption: true
            },
            () => {
                this.onChange();
            }
        );
    }

    handleToOptionChange(selectedFromToOption: ToRowData, selectedToOption: string): void {
        this.setState(
            {
                selectedToOption: selectedFromToOption as FromToRowData,
                selectedToOptionValue: selectedToOption,
                hasFromOption: true
            },
            () => {
                this.onChange();
            }
        );
    }

    handleToOnlyOptionChange(selectedToOnlyOption: ToRowData, selectedToOption: string): void {
        this.setState(
            {
                selectedToOnlyOption: selectedToOnlyOption,
                selectedToOnlyOptionValue: selectedToOption,
                selectedFromToOption: null,
                selectedFromToOptionValue: null,
                selectedToOption: null,
                selectedToOptionValue: null,
                hasFromOption: false,
            },
            () => {
                this.onChange();
            }
        );
    }

    onChange() {
        this.saveSelection();
        const url = this.getSelectedUrl(
            this.state.selectedProcess.process,
            this.state.selectedFromToOptionValue,
            this.state.selectedToOptionValue ?? this.state.selectedToOnlyOptionValue
        );
        this.props.onChange(url);
    }

    private getSelectedUrl(selectedProcess: string, selectedFromToOptionValue: string, selectedToOptionValue: string) {
        const url = getURL(selectedProcess, selectedFromToOptionValue, selectedToOptionValue);
        return url;
    }

    private onSubmit() {
        const url = this.getSelectedUrl(
            this.state.selectedProcess.process,
            this.state.selectedFromToOptionValue,
            this.state.selectedToOptionValue ?? this.state.selectedToOnlyOptionValue
        );
        console.log(url);
        if (this.props.onSubmit) {
            this.props.onSubmit(url);
        }
    }

    render() {
        return <>
            <div className={styles.container}>
                <div className={styles.row}>
                    <div className={styles.col}>
                        <label htmlFor="processType" className={styles.label}>I want to:</label>
                        <ProcessSelectionDropdown id="processType"
                            options={this.props.options.sort((a, b) => a.processName.localeCompare(b.processName))}
                            selectedOption={this.state.selectedProcess}
                            formatOption={(v) => v && v.processName || '--'}
                            onChange={(v) => this.handleProcessTypeChange(v)}
                        >
                        </ProcessSelectionDropdown>
                    </div>
                    {this.state.selectedProcess && this.state.hasFromOption &&
                        <div className={styles.col}>
                            <label htmlFor="fromType" className={styles.label}>From:</label>
                            <SearchableDropdown
                                id="fromType"
                                options={this.state.selectedProcess.fromToMediaOptions || []}
                                selectedOption={this.state.selectedFromToOption}
                                selectedFromOption={this.state.selectedFromToOptionValue}
                                formatOption={(v) => v && v.mediaName || '--'}
                                formatFromOption={(item) => item?.name || '--'}
                                onChange={(selectedFromToOption, selectedFromOption) => this.handleFromToOptionChange(selectedFromToOption, selectedFromOption)}
                            />
                        </div>
                    }

                    {
                        this.state.selectedProcess && this.state.hasFromOption && <div className={styles.col}>
                            <label htmlFor="toType" className={styles.label}>To:</label>

                            <SearchableDropdown
                                options={this.state.selectedFromToOption?.toOptions || []}
                                selectedOption={this.state.selectedToOption}
                                selectedFromOption={this.state.selectedToOptionValue}
                                formatOption={(v) => v && v.mediaName || '--'}
                                formatFromOption={(item) => item?.name || '--'}
                                onChange={(v, f) => this.handleToOptionChange(v, f)}
                            />
                        </div>
                    }
                    {
                        this.state.selectedProcess && !this.state.hasFromOption && <div className={styles.col}>
                            <label htmlFor="toType" className={styles.label}>To:</label>

                            <SearchableDropdown
                                options={this.state.selectedProcess.toOnlyMediaOptions || []}
                                selectedOption={this.state.selectedToOnlyOption}
                                selectedFromOption={this.state.selectedToOnlyOptionValue}
                                formatOption={(v) => v && v.mediaName || '--'}
                                formatFromOption={(item) => item?.name || '--'}
                                onChange={(v, f) => this.handleToOnlyOptionChange(v, f)}
                            />
                        </div>
                    }

                    <div className={styles.col}>
                        <label htmlFor="proceedButton" className={styles.label}>&nbsp;</label>
                        <button id="proceedButton" className={styles['submit-btn']} onClick={() => this.onSubmit()}>
                            Start Now <img width="16" height="16" src="/icons/svg/arrow-right-short-shaft.svg" className="color-invert" alt="" />
                        </button>
                    </div>
                </div>
                <div id="selectionFeedback" className={styles.feedback}>
                    100% Free, Secure & High Quality Conversions
                </div>
            </div>
        </>
    }
}

